import { CheckCircleOutline, ErrorOutlineOutlined, WarningAmberOutlined } from '@mui/icons-material'
import { Box, Skeleton, Typography } from '@mui/material'
import { useTexts } from '../../useTexts'
import { InputError } from '../../hooks/useController'

interface StorageAlertProps {
  percentage: number
  newPlanningRange: number
  isLoading: boolean
  isDragging: boolean
  inputError?: InputError
  storageError?: InputError
}

export const StorageAlert = ({
  percentage,
  newPlanningRange,
  isLoading,
  isDragging,
  inputError = { hasError: false, message: '' },
  storageError = { hasError: false, message: '' },
}: StorageAlertProps) => {
  const texts = useTexts()

  const renderContent = () => {
    if (isLoading || isDragging) {
      return (
        <>
          <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
          <Skeleton variant="text" width={350} height={20} />
        </>
      )
    }

    // Handle input errors first (manual input validation)
    if (inputError.hasError) {
      return (
        <>
          <ErrorOutlineOutlined sx={{ color: 'error.main', mr: 1, fontSize: 20 }} />
          <Typography variant="body2" color="error.dark">
            {inputError.message}
          </Typography>
        </>
      )
    }

    // Handle storage errors (percentage validation)
    if (storageError.hasError) {
      return (
        <>
          <ErrorOutlineOutlined sx={{ color: 'error.main', mr: 1, fontSize: 20 }} />
          <Typography variant="body2" color="error.dark">
            {storageError.message}
          </Typography>
        </>
      )
    }

    // If no errors, show storage alerts based on percentage
    if (percentage >= 95) {
      return (
        <>
          <ErrorOutlineOutlined sx={{ color: 'error.main', mr: 1, fontSize: 20 }} />
          <Typography variant="body2" color="error.dark">
            {texts.rangePickerAlertwithError(newPlanningRange, percentage)}
          </Typography>
        </>
      )
    }

    if (percentage >= 80) {
      return (
        <>
          <WarningAmberOutlined sx={{ color: 'warning.main', mr: 1, fontSize: 20 }} />
          <Typography variant="body2" color="text.primary">
            {texts.rangePickerAlertwithWarning(newPlanningRange, percentage)}
          </Typography>
        </>
      )
    }

    return (
      <>
        <CheckCircleOutline sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
        <Typography variant="body2" color="text.secondary">
          {texts.rangePickerAlertwithSuccess(newPlanningRange, percentage)}
        </Typography>
      </>
    )
  }

  return <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 4 }}>{renderContent()}</Box>
}

export default StorageAlert
